import { useEffect, useState } from "react";

/**
 * Tracks which pages of a PDF are close enough to the viewport to be worth
 * rendering, so callers can mount <Page> only for those and leave the rest as
 * cheap sized placeholders.
 *
 * Pairs with PDF_LOAD_OPTIONS: pdf.js only fetches the byte ranges backing the
 * pages it actually renders, so windowing here is what keeps a large document
 * from downloading in full.
 *
 * Every observed element must carry a `data-window-key` attribute holding the
 * numeric key the caller wants back. That is usually the page number, but for
 * lists rendered in a user-defined order it should be the element's position,
 * since position is what the observer actually reports on.
 *
 * @param {Element|null} rootEl        The scrolling container. Also the element
 *                                     queried for targets, so it must contain them.
 * @param {number|null}  itemCount     Number of items rendered; re-attaches the
 *                                     observer when it changes.
 * @param {object}       [options]
 * @param {string}       [options.selector=".pdf-page"]
 * @param {string}       [options.rootMargin="150% 0px"] How far outside the
 *                       viewport to pre-render. Percentages are relative to the
 *                       root's size.
 * @param {number[]}     [options.initialKeys=[1]] Seeded on mount so the first
 *                       page paints without waiting for the observer's first
 *                       callback.
 * @param {string|number} [options.observationKey=itemCount] Re-observes when a
 *                        filtered list changes without changing its length.
 * @returns {Set<number>} Keys whose canvases should currently be mounted.
 */
export const usePdfPageWindow = (
  rootEl,
  itemCount,
  {
    selector = ".pdf-page",
    rootMargin = "150% 0px",
    initialKeys = [1],
    observationKey = itemCount,
  } = {}
) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps -- initializer only
  const [visibleKeys, setVisibleKeys] = useState(() => new Set(initialKeys));

  useEffect(() => {
    if (!rootEl || !itemCount) return;

    const targets = rootEl.querySelectorAll(selector);
    if (!targets.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        setVisibleKeys((prev) => {
          const next = new Set(prev);
          let changed = false;

          entries.forEach((entry) => {
            const key = Number(entry.target.getAttribute("data-window-key"));
            if (!Number.isFinite(key)) return;

            if (entry.isIntersecting) {
              if (!next.has(key)) {
                next.add(key);
                changed = true;
              }
            } else if (next.delete(key)) {
              changed = true;
            }
          });

          return changed ? next : prev;
        });
      },
      { root: rootEl, rootMargin, threshold: 0 }
    );

    targets.forEach((el) => io.observe(el));

    return () => io.disconnect();
  }, [rootEl, itemCount, observationKey, selector, rootMargin]);

  return visibleKeys;
};

export default usePdfPageWindow;
